import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { AdministratorsService } from './configurators-2d/administrations/administrators-service';
import { Application } from '../models/application';
import { TranslateService } from '@ngx-translate/core';
import { CONFIG_CONTEXT } from '../enum/config-context.enum';
import { logError } from './helpers/sentry.helper';
import { FeatureSettingsService } from './feature-settings/feature-settings.service';

@Injectable({
  providedIn: 'root',
})
export class DatasService {
  private readonly http = inject(HttpClient);
  private readonly administratorsService = inject(AdministratorsService);
  private readonly featureSettingsService = inject(FeatureSettingsService);
  private readonly translate = inject(TranslateService);

  styles: any;
  models: any;
  layouts: any;
  ovenConfigurations: any;
  fridgeConfigurations: any;
  storeList: any;
  private _productsPmmEanBridge: any[];
  private readonly administrationOnlyApplications = [
    CONFIG_CONTEXT.CASTOMETRE,
    CONFIG_CONTEXT.CASTOFID_MINI,
  ];
  featureSettingsAdminEnabled = false;
  demoVideosAdminEnabled = false;

  kitchenEnabled: boolean = false;
  kitchenV2Enabled: boolean = false;
  bathroomEnabled: boolean = false;
  bathroomV2Enabled: boolean = false;
  storageEnabled: boolean = false;

  storage: any;

  readonly applications = new Map<string, Application>();
  private isLoading = false;

  public init = (): Promise<void> => {
    return new Promise((resolve: any, reject: any) => {
      this.http.get('datas/kitchen.json?v=' + environment.version).subscribe({
        next: (responseDatas: any) => {
          this.http.get(environment.byMe.storage.datas + '?v=' + environment.version).subscribe({
            next: (responseStorageDatas: any) => {
              this.styles = responseDatas.styles;
              this.models = responseDatas.models;
              this.layouts = responseDatas.layouts;
              this.ovenConfigurations = responseDatas.ovenConfigurations;
              this.fridgeConfigurations = responseDatas.fridgeConfigurations;
              this.storage = responseStorageDatas.data;
              this.storage.version = responseStorageDatas.version;

              // si c'est l'app Vendor
              if (environment.vendor) {
                this.http.get('datas/stores.json?noCache=' + Math.random()).subscribe({
                  next: (responseDatas2: any) => {
                    this.storeList = responseDatas2.storeList.sort((a, b) => {
                      if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
                      if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
                      return 0;
                    });
                    this.http.get('datas/products-pmm.json?noCache=' + Math.random()).subscribe({
                      next: (responseDatas3: any) => {
                        this._productsPmmEanBridge = responseDatas3;
                        resolve();
                      },
                      error: (error: any) => reject(error),
                    });
                  },
                  error: (error: any) => reject(error),
                });
              }
              // Si c'est l'app Customer
              else {
                // Configurateur 3D
                this.kitchenEnabled = environment.byMe.kitchen.isCustomerOpened;
                this.storageEnabled = environment.byMe.storage.isCustomerOpened;
                this.bathroomEnabled = environment.byMe.bathroom.isCustomerOpened;

                // Configurateur 2D
                this.customerEnableApplications();

                resolve();
              }
            },
            error: (error: any) => reject(error),
          });
        },
        error: (error: any) => reject(error),
      });
    });
  };

  getPmmForEan(ean: string) {
    let _eanRow = this._productsPmmEanBridge.find(pRow => pRow[0] === ean);
    if (_eanRow) {
      return _eanRow[1];
    }
    return 'ND';
  }

  /**
   * Applications activées pour les vendeurs
   * @param vendorSapId
   * @param catalogs
   */
  public async enableApplications(vendorSapId: string, catalogs: any) {
    this.kitchenEnabled = catalogs.includes('K');
    this.kitchenV2Enabled = catalogs.includes('K2');

    this.storageEnabled = catalogs.includes('S');

    this.bathroomEnabled = catalogs.includes('B');
    this.bathroomV2Enabled = catalogs.includes('B2');

    if (this.kitchenV2Enabled || this.kitchenEnabled) {
      let app = new Application(CONFIG_CONTEXT.KITCHEN, true);
      app.isTopLevel = false;
      this.applications.set(app.distribID, app);
    }

    if (this.storageEnabled) {
      let app = new Application(CONFIG_CONTEXT.STORAGE, true);
      app.isTopLevel = false;
      this.applications.set(app.distribID, app);
    }

    // Affichage des applications activées pour le magasin
    Object.entries(environment.configurators2d.applications).forEach(([, app2d]) => {
      const app = new Application(app2d.distribID);
      app.isInDropdown = app2d['isInDropdown'] ?? true; // on met par défaut
      this.applications.set(app.distribID, app);

      const children = app2d['children'];
      if (children) {
        app.children = [];
        for (const child of children) {
          let child2d = new Application(child['distribID'], catalogs.includes(child['appCode']));
          child2d.isTopLevel = false;
          app.children.push(child2d);
          this.applications.set(child2d.distribID, child2d);
        }

        app.appEnabled = app.children.some(c => c.appEnabled);
      } else {
        app.appEnabled = catalogs.includes(app2d.appCode);
      }
    });
    this.enableAdministrationOnlyApplications();

    // Récupération des droits d'administration par application
    this.isLoading = true;
    if (!environment.configurators2d.global.debug.forceAdmin) {
      try {
        const apps = await this.administratorsService.byUser(vendorSapId);
        this.demoVideosAdminEnabled = apps.length > 0;
        apps.forEach(appAdmin => {
          const app = this.applications.get(appAdmin.distribId);
          if (app) app.adminEnabled = true;
        });
      } catch (err) {
        this.demoVideosAdminEnabled = false;
        logError(err);
      } finally {
        await this.enableFeatureSettingsAdministration();
        this.isLoading = false;
      }
    } else {
      this.applications.forEach(app => app.adminEnabled = true);
      this.demoVideosAdminEnabled = true;
      await this.enableFeatureSettingsAdministration();
      this.isLoading = false;
    }
  }

  private async enableFeatureSettingsAdministration(): Promise<void> {
    try {
      this.featureSettingsService.resetCanAdminister();
      this.featureSettingsAdminEnabled = await this.featureSettingsService.canAdminister();
    } catch (err) {
      this.featureSettingsAdminEnabled = false;
      console.error(err);
    }
  }

  public isAppEnabled(distribId: string): boolean {
    return this.applications.get(distribId)?.appEnabled ?? false;
  }

  public isInDropdown(distribId: string): boolean {
    return this.isAppEnabled(distribId)
      && (this.applications.get(distribId)?.isInDropdown ?? false);
  }

  public get topLevelAppsEnabled(): string[] {
    return [...this.applications.entries()]
      .filter(elem => elem[1].isTopLevel && elem[1].appEnabled && !elem[1].isAdministrationOnly)
      .map(elem => elem[0]);
  }

  public get appsEnabled(): string[] {
    return [...this.applications.entries()]
      .filter(elem => !elem[1].children && elem[1].appEnabled && !elem[1].isAdministrationOnly)
      .map(elem => elem[0]);
  }

  public get appsAdminEnabled(): string[] {
    return [...this.applications.entries()]
      .filter(elem => !elem[1].children && elem[1].appEnabled && elem[1].adminEnabled)
      .map(elem => elem[0]);
  }

  private enableAdministrationOnlyApplications(): void {
    this.administrationOnlyApplications.forEach(distribId => {
      const app = new Application(distribId, true);
      app.isTopLevel = false;
      app.isInDropdown = false;
      app.isAdministrationOnly = true;
      this.applications.set(app.distribID, app);
    });
  }

  public get isDataLoading() {
    return this.isLoading;
  }

  public getStorageGtmDimentions(storage: any): any {
    let _storage: any = { config_type: CONFIG_CONTEXT.STORAGE };

    if (storage.inspirationId === 0) {
      _storage.storage_inspiration = 'AUCUNE';
      return _storage;
    } else if (storage.inspirationId > 0) {
      _storage.storage_inspiration = this.storage.inspirations.find(i => i.id === storage.inspirationId)?.sheet;
    }

    if (storage.boxId === 0) {
      _storage.storage_box = 'Tous';
    } else if (storage.boxId > 0) {
      _storage.storage_caisson = this.storage.boxes.find(b => b.id === storage.boxId)?.label;
    }

    if (storage.frontId === 0) {
      _storage.storage_box = 'Tous';
    } else if (storage.frontId > 0) {
      _storage.storage_front = this.storage.fronts.find(f => f.id === storage.frontId)?.label;
    }

    if (storage.handlesId === 0) {
      _storage.storage_handles = 'Tous';
    } else if (storage.handlesId > 0) {
      _storage.storage_handles = this.storage.handles.find(h => h.id === storage.handlesId)?.label;
    }

    if (storage.legsId === 0) {
      _storage.storage_legs = 'Tous';
    } else if (storage.legsId > 0) {
      _storage.storage_legs = this.storage.legs.find(l => l.id === storage.legsId)?.label;
    }

    return _storage;
  }

  public getKitchenGtmDimentions(kitchen: any): any {
    let _kitchen: any = { config_type: CONFIG_CONTEXT.KITCHEN };

    if (kitchen.styleId === 0) {
      _kitchen.kitchen_ambiance = 'Tous';
    } else if (kitchen.styleId > 0) {
      let _kStyle = this.styles.find(s => s.id === kitchen.styleId);
      _kitchen.kitchen_ambiance = this.translate.instant(_kStyle.titleRef);
    }

    if (kitchen.modelId === 0) {
      _kitchen.kitchen_model = 'Tous';
    } else if (kitchen.modelId > 0) {
      let _kModel = this.models.find(m => m.id === kitchen.modelId);
      _kitchen.kitchen_model = _kModel.title;
    }

    if (kitchen.layoutRef) {
      _kitchen.kitchen_layout = kitchen.layoutRef;
      if (_kitchen.kitchen_layout === '') _kitchen.kitchen_layout = 'Tous';
    }

    if (kitchen.ovenConfigurationRef === '') {
      _kitchen.kitchen_oven = 'Tous';
    } else if (kitchen.ovenConfigurationRef) {
      let _kOvenConf = this.ovenConfigurations.find(oc => oc.id === kitchen.ovenConfigurationRef);
      _kitchen.kitchen_oven = this.translate.instant(_kOvenConf.titleRef);
    }

    if (kitchen.fridgeConfigurationRef === '') {
      _kitchen.kitchen_fridge = 'Tous';
    } else if (kitchen.fridgeConfigurationRef) {
      let _kFridgeConf = this.fridgeConfigurations.find(fc => fc.id === kitchen.fridgeConfigurationRef);
      _kitchen.kitchen_fridge = this.translate.instant(_kFridgeConf.titleRef);
    }

    return _kitchen;
  }

  public storageInspiration = {
    getBoxes: (inspirationId: number): any[] => {
      return this.storage.inspirationDeclinations
        .filter((row: any) => {
          return row.iId === inspirationId;
        })
        .map((row: any) => {
          return row.bId;
        })
        .filter((boxId, index, noDouble) => {
          let _index = noDouble.indexOf(boxId);
          return _index === index;
        })
        .sort()
        .map((boxId: number) => {
          return this.storage.boxes.find((row: any) => {
            return row.id === boxId;
          });
        });
    },
    getFronts: (inspirationId: number, boxId: number): any[] => {
      return this.storage.inspirationDeclinations
        .filter((row: any) => {
          if (row.iId !== inspirationId) return false;
          return row.bId === boxId;
        })
        .map((row: any) => {
          return row.fId;
        })
        .filter((frontId, index, noDouble) => {
          let _index = noDouble.indexOf(frontId);
          return _index === index;
        })
        .sort()
        .map((frontId: number) => {
          return this.storage.fronts.find((row: any) => {
            return row.id === frontId;
          });
        });
    },
    getHandles: (inspirationId: number, boxId: number, frontId: number): any[] => {
      return this.storage.inspirationDeclinations
        .filter((row: any) => {
          if (row.iId !== inspirationId) return false;
          if (row.bId !== boxId) return false;
          return row.fId === frontId;
        })
        .map((row: any) => {
          return row.hId;
        })
        .filter((handlesId, index, noDouble) => {
          let _index = noDouble.indexOf(handlesId);
          return _index === index;
        })
        .sort()
        .map((handlesId: number) => {
          return this.storage.handles.find((row: any) => {
            return row.id === handlesId;
          });
        });
    },
    getLegs: (inspirationId: number, boxId: number, frontId: number, handlesId: number): any[] => {
      return this.storage.inspirationDeclinations
        .filter((row: any) => {
          if (row.iId !== inspirationId) return false;
          if (row.bId !== boxId) return false;
          if (row.fId !== frontId) return false;
          return row.hId === handlesId;
        })
        .map((row: any) => {
          return row.lId;
        })
        .filter((legsId, index, noDouble) => {
          let _index = noDouble.indexOf(legsId);
          return _index === index;
        })
        .sort()
        .map((legsId: number) => {
          return this.storage.legs.find((row: any) => {
            return row.id === legsId;
          });
        });
    },
  };

  /**
   * Applications activées pour les clients
   */
  public customerEnableApplications() {
    Object.entries(environment.configurators2d.applications).forEach(([, app]) => {
      let children = app['children'];
      if (children) {
        for (const child of children) {
          let child2d = new Application(child.distribID, child.isCustomerOpened);
          this.applications.set(child2d.distribID, child2d);
        }
      } else {
        let app2d = new Application(app.distribID);
        app2d.appEnabled = app.isCustomerOpened;
        this.applications.set(app2d.distribID, app2d);
      }
    });
  }

  /**
   * Donne les applis actives pour les customers
   */
  public get customerAppsEnabled(): string[] {
    return [...this.applications.entries()].filter(elem => elem[1].appEnabled).map(elem => elem[0]);
  }
}
